import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
    AnimatedPage,
    AnimatedSection,
    metricMotion,
} from '../components/animations/MotionContainers';
import { SummaryMetricCard } from '../components/dashboard/SummaryMetricCard';
import { EntradasTable } from '../components/entradas/EntradasTable';
import { VariedadNavAutocomplete } from '../components/entradas/VariedadNavAutocomplete';
import { useFlujosCatalogs } from '../hooks/useFlujosCatalogs';
import { useFlujosEntradas } from '../hooks/useFlujosEntradas';
import { useFlujosVariedadesNav } from '../hooks/useFlujosVariedadesNav';
import { getDefaultEntryDateFilters } from '../utils/dashboardData';
import {
    calculateFlowTotals,
    formatKilos,
    formatPercent,
} from '../utils/flujoCalculations';
import { exportEntradasToExcel } from '../utils/entradasExcel';
import { usuarios } from '../utils/mockFlujosData';

export const ConsultaEntradas = () => {
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();
    const {
        almacenes,
        variedades: variedadesFlujos,
        cajasPalet,
        loading: catalogsLoading,
        error: catalogsError,
    } = useFlujosCatalogs();
    const {
        rows: variedadesNav,
        loading: variedadesLoading,
        error: variedadesError,
    } = useFlujosVariedadesNav();
    const [filters, setFilters] = useState(() => ({
        search: '',
        ...getDefaultEntryDateFilters(),
        idVariedad: '',
        idAlmacenes: [],
    }));
    const entradaParams = useMemo(() => ({
        page: 1,
        limit: 500,
        search: filters.search.trim() || undefined,
        desde: filters.desde || undefined,
        hasta: filters.hasta || undefined,
        codigo_variedad: filters.idVariedad || undefined,
        ids_almacenes: filters.idAlmacenes.length > 0 ? filters.idAlmacenes.join(',') : undefined,
        sortBy: 'fecha',
        sortDirection: 'desc',
    }), [filters]);
    const { rows: filteredRows, loading: entradasLoading, error: entradasError } = useFlujosEntradas(
        entradaParams,
        { almacenes, variedades: variedadesFlujos, cajasPalet }
    );

    const totals = calculateFlowTotals(filteredRows);
    const filteredListAnimationKey = [
        filters.search,
        filters.desde,
        filters.hasta,
        filters.idVariedad,
        filters.idAlmacenes.join('-'),
    ].join('|');

    const updateFilter = (field) => (event) => {
        setFilters((current) => ({
            ...current,
            [field]: event.target.value,
        }));
    };

    const toggleWarehouse = (warehouseId) => {
        setFilters((current) => {
            if (current.idAlmacenes.includes(warehouseId)) {
                return {
                    ...current,
                    idAlmacenes: current.idAlmacenes.filter((id) => id !== warehouseId),
                };
            }

            const nextSelectedWarehouseIds = [...current.idAlmacenes, warehouseId];

            return {
                ...current,
                idAlmacenes: nextSelectedWarehouseIds.length === almacenes.length ? [] : nextSelectedWarehouseIds,
            };
        });
    };

    const allWarehousesSelected = filters.idAlmacenes.length === 0;
    const handleExportExcel = () => {
        exportEntradasToExcel(filteredRows, {
            warehouses: almacenes,
            users: usuarios,
            cajasPalet,
        });
    };

    return (
        <AnimatedPage>
                <Stack spacing={2.5}>
                    <AnimatedSection>
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flex: '0 0 auto',
                                    }}
                                >
                                    <FactCheckOutlinedIcon />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h1">Consulta de entradas</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Busqueda y revision de entradas registradas
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </AnimatedSection>

                    <AnimatedSection>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                                <FilterAltOutlinedIcon color="primary" fontSize="small" />
                                <Typography sx={{ color: 'primary.main', fontSize: 16, fontWeight: 900 }}>
                                    Filtros
                                </Typography>
                            </Stack>
                            <Grid container spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        label="Buscar"
                                        placeholder="Codigo o variedad"
                                        value={filters.search}
                                        onChange={updateFilter('search')}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchOutlinedIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Desde"
                                        value={filters.desde}
                                        onChange={updateFilter('desde')}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Hasta"
                                        value={filters.hasta}
                                        onChange={updateFilter('hasta')}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <VariedadNavAutocomplete
                                        options={variedadesNav}
                                        value={filters.idVariedad}
                                        onChange={(value) => setFilters((current) => ({
                                            ...current,
                                            idVariedad: value,
                                        }))}
                                        loading={variedadesLoading}
                                        placeholder="Todas las variedades"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 3.5 }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={0}
                                            sx={{
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={0.75}
                                                sx={{ alignItems: 'center', color: 'text.secondary', minWidth: 'max-content' }}
                                            >
                                                <WarehouseOutlinedIcon color="primary" fontSize="medium" />
                                                <Box>
                                                    <Typography sx={{ color: 'primary.main', fontSize: 12, fontWeight: 900, lineHeight: '15px' }}>
                                                        Almacenes
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: '14px' }}>
                                                        Filtrar consulta
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    flexWrap: 'wrap',
                                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                                    gap: 1,
                                                }}
                                            >
                                                <Button
                                                    variant={allWarehousesSelected ? 'contained' : 'outlined'}
                                                    color={allWarehousesSelected ? 'primary' : 'secondary'}
                                                    size="small"
                                                    onClick={() => setFilters((current) => ({ ...current, idAlmacenes: [] }))}
                                                    sx={{ minHeight: 34, px: 1.5, fontWeight: 900 }}
                                                >
                                                    Todos
                                                </Button>
                                                {almacenes.map((almacen) => {
                                                    const active = filters.idAlmacenes.includes(almacen.id);

                                                    return (
                                                        <Button
                                                            key={almacen.id}
                                                            variant={active ? 'contained' : 'outlined'}
                                                            color={active ? 'primary' : 'secondary'}
                                                            size="small"
                                                            onClick={() => toggleWarehouse(almacen.id)}
                                                            sx={{ minHeight: 34, px: 1.5, fontWeight: 900 }}
                                                        >
                                                            {almacen.nombre}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 1 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<FileDownloadOutlinedIcon />}
                                        disabled={entradasLoading || catalogsLoading || filteredRows.length === 0}
                                        onClick={handleExportExcel}
                                        sx={{
                                            minHeight: 40,
                                            px: 1.5,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Excel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </AnimatedSection>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<FactCheckOutlinedIcon />}
                                    title="Registros"
                                    value={filteredRows.length}
                                    helper="Resultado filtrado"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<FactCheckOutlinedIcon />}
                                    title="Kg volcados"
                                    value={formatKilos(totals.kilosVolcados)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<FactCheckOutlinedIcon />}
                                    title="Kg clasificados"
                                    value={formatKilos(totals.kilosClasificados)}
                                    suffix="kg"
                                />
                            </AnimatedSection>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                            <AnimatedSection variant={metricMotion}>
                                <SummaryMetricCard
                                    icon={<FactCheckOutlinedIcon />}
                                    title="Rendimiento"
                                    value={formatPercent(totals.rendimiento)}
                                    suffix="%"
                                />
                            </AnimatedSection>
                        </Grid>
                    </Grid>

                    {(entradasLoading || catalogsLoading || variedadesLoading || entradasError || catalogsError || variedadesError) && (
                        <Alert
                            severity={entradasError || catalogsError || variedadesError ? 'error' : 'info'}
                            variant="outlined"
                        >
                            {entradasError || catalogsError || variedadesError || 'Cargando datos...'}
                        </Alert>
                    )}

                    <AnimatedSection>
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div
                                key={filteredListAnimationKey}
                                initial={reduceMotion ? false : { opacity: 0, y: 12, filter: 'blur(2px)' }}
                                animate={reduceMotion ? undefined : { opacity: 1, y: 0, filter: 'blur(0px)' }}
                                exit={reduceMotion ? undefined : { opacity: 0, y: -8, filter: 'blur(1px)' }}
                                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <EntradasTable
                                    rows={filteredRows}
                                    warehouses={almacenes}
                                    users={usuarios}
                                    onRowClick={(row) => navigate(`/consulta-entradas/${row.id || row.codigo}`)}
                                />
                            </motion.div>
                        </AnimatePresence>
                    </AnimatedSection>
                </Stack>
        </AnimatedPage>
    );
};
